import { NODES_BASE_KEY } from '@/api/nodes/helpers/base-key'
import { INode } from '@/api/nodes/mutations/directory/create-directory-mutation'
import { IInfiniteGetNodeContentQueryData } from '@/api/nodes/queries/get-node-content-query'
import { updateInfiniteNodesOptimistically } from '@/api/nodes/helpers/update-infinite-nodes-optimistically'
import { IApi } from '@/setup/api-client'
import { useApiMutation } from '@/utils/api/api-mutation'
import { QueryKey } from '@tanstack/react-query'
import { useApiToolbox } from '@/utils/api/use-api-toolbox'
import type { IMoveNodesPayload } from 'api/dist/routes/organization/[organization_id]/brands/[brand_slug]/nodes/node/move'

interface IMoveNodesParams {
    organizationId: string
    brandSlug: string
}

export class MoveNodesMutation {
    static call(api: IApi, { organizationId, brandSlug, ...payload }: IMoveNodesParams & IMoveNodesPayload) {
        return api.post<INode, IMoveNodesPayload>(
            `/organization/${organizationId}/brands/${brandSlug}/nodes/node/move`,
            payload
        )
    }
}

interface IUseMoveNodeMutationParams {
    nodeContentQueryKey: QueryKey
    sendToastSuccess: (count: number) => unknown
    sendToastError: (count: number) => unknown
}

export const useMoveNodesMutation = ({
    nodeContentQueryKey,
    sendToastError,
    sendToastSuccess,
}: IUseMoveNodeMutationParams) => {
    const { api, apiParams, qc } = useApiToolbox()

    return useApiMutation({
        mutationFn: (rawPayload: IMoveNodesPayload) => {
            // Ensure paths are unique
            const payload = { ...rawPayload, paths: Array.from(new Set(rawPayload.paths)) }

            const promise = MoveNodesMutation.call(api, {
                ...payload,
                organizationId: apiParams.organizationId,
                brandSlug: apiParams.brandSlug,
            })

            let prev: IInfiniteGetNodeContentQueryData | undefined

            updateInfiniteNodesOptimistically({
                qc,
                nodeContentQueryKey,
                onSuccess: sendToastSuccess,

                reducePage: (acc, node, _i, _a, { nodesCount }) => {
                    const isMovedNode = payload.paths.includes(node.path)

                    if (isMovedNode) return acc

                    const isReceivingNode = payload.newDirectoryPath === node.path && node.type === 'DIRECTORY'

                    if (isReceivingNode) {
                        return [
                            ...acc,
                            {
                                ...node,
                                numberOfItems: (node.numberOfItems ?? 0) + nodesCount,
                            },
                        ] satisfies INode[]
                    }

                    return [...acc, node]
                },

                assignPrev: (prevData) => {
                    prev = prevData
                },

                count: payload.paths.length,
            })

            return promise.then((res) => {
                // Invalidation promise must be returned
                return qc
                    .invalidateQueries({
                        predicate: (query) => NODES_BASE_KEY.every((key, i) => query.queryKey[i] === key),
                    })
                    .catch((e) => {
                        sendToastError(payload.paths.length)

                        qc.setQueryData<IInfiniteGetNodeContentQueryData>(nodeContentQueryKey, prev)
                        qc.refetchQueries({
                            predicate: (query) => NODES_BASE_KEY.every((key, i) => query.queryKey[i] === key),
                        })

                        throw e
                    })
                    .finally(() => {
                        return res
                    })
            })
        },
    })
}
